"use client";

import { useEffect, useState } from "react";
import { io, type Socket } from "socket.io-client";
import { api, loadTokens, type SharePrefs } from "@/lib/api";
import { positionValueCents } from "@/lib/format";
import { WinShareModal } from "./WinShareModal";

/**
 * EPIC-006 STORY-610 — auto-trigger win-share modal on resolution.
 *
 * Mounted in the root layout. Skipped (`null`) for anonymous viewers
 * + when `share_prefs.promptsEnabled === false`.
 *
 * How it works:
 *   1. On mount, fetch the user's open + recently-resolved positions.
 *   2. Subscribe to the `market:${slug}` room for each unique market.
 *   3. On `market:resolved`, look up the user's position for that
 *      market and decide if it was a winner (resolved_outcome_id ===
 *      position.outcomeId AND shares > 0).
 *   4. If yes, open `<WinShareModal>` with the win details. Suppress
 *      duplicate triggers (per-position) so users don't see the modal
 *      twice for the same resolution.
 *
 * Why not poll: closed-beta scale has 1-3 resolutions per day. The
 * existing socket emit (admin.service.ts → markets.gateway) is the
 * source of truth; polling adds nothing.
 *
 * Manual "Share win" button on `<DashboardTabs>` still works the
 * same way — that path is for users who skipped the auto-prompt or
 * came back to share later.
 */

interface WinShareState {
  amountCents: number;
  marketTitle: string;
  marketSlug: string;
  outcome: "YES" | "NO";
  winType: "small" | "big" | "milestone";
}

interface MarketResolvedEvent {
  marketId: string;
  marketSlug: string;
  outcome: string;
  resolvedOutcomeId: string | null;
  resolvedAt: string;
}

function classifyWinType(amountCents: number): "small" | "big" | "milestone" {
  if (amountCents >= 100_000) return "milestone";
  if (amountCents >= 50_000) return "big";
  return "small";
}

export function WinAutoTrigger() {
  const [activeWin, setActiveWin] = useState<WinShareState | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<SharePrefs | null>(null);

  useEffect(() => {
    if (!loadTokens()) return;
    let cancelled = false;
    void Promise.all([api.me(), api.getSharePrefs()])
      .then(([me, p]) => {
        if (cancelled) return;
        setUsername(me.username);
        setPrefs(p);
      })
      .catch(() => {
        // Anonymous or share_prefs unreachable — auto-trigger stays
        // disabled. Manual button on DashboardTabs still works.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!prefs || !prefs.promptsEnabled) return;
    if (!username) return;
    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    if (!apiBase) return;
    const url = apiBase.replace(/\/$/, "");

    let socket: Socket | null = null;
    let cancelled = false;
    // Track positions by market slug → outcomeId so we can decide
    // winner-or-not in O(1) on the resolution event. Also track
    // already-fired markets so a reconnect doesn't re-trigger.
    const positionsByMarket = new Map<
      string,
      {
        outcomeId: string;
        outcomeSortOrder: number;
        marketTitle: string;
        avgCostCents: bigint;
        shares: bigint;
      }
    >();
    const firedMarkets = new Set<string>();

    async function bootstrap() {
      try {
        const positions = await api.listMyPositions();
        if (cancelled) return;
        for (const p of positions) {
          // Only listen for active (unresolved) markets — already-
          // resolved positions get the manual button on /home.
          if (p.marketStatus === "resolved") continue;
          if (Number(p.shares) <= 0) continue;
          // First position per market wins the entry — multiple
          // positions on the same market under different outcomes
          // are rare in practice and the auto-trigger only needs to
          // know which outcome the user holds.
          if (!positionsByMarket.has(p.marketSlug)) {
            positionsByMarket.set(p.marketSlug, {
              outcomeId: p.outcomeId,
              outcomeSortOrder: p.outcomeSortOrder,
              marketTitle: p.marketTitle.en ?? p.marketSlug,
              avgCostCents: BigInt(p.avgCostCents),
              shares: BigInt(p.shares),
            });
          }
        }
        if (positionsByMarket.size === 0 || cancelled) return;

        socket = io(url, {
          transports: ["websocket", "polling"],
          reconnection: true,
          reconnectionAttempts: 10,
        });
        socket.on("connect", () => {
          for (const slug of positionsByMarket.keys()) {
            socket!.emit("subscribe", { slug });
          }
        });
        socket.on("market:resolved", (event: MarketResolvedEvent) => {
          if (firedMarkets.has(event.marketSlug)) return;
          const pos = positionsByMarket.get(event.marketSlug);
          if (!pos) return;
          // Refund (no winner) → don't fire.
          if (event.resolvedOutcomeId === null) return;
          // Wrong outcome → user lost; don't fire.
          if (event.resolvedOutcomeId !== pos.outcomeId) return;

          // Winner. Per migration 0145 centi-share semantics:
          //   payoutCents (USDC cents) = centi × 100¢/share / 100 = centi
          //   costBasisCents (USDC cents) = centi × avg / 100  (positionValueCents)
          // 2026-05-11 founder report — these lines previously omitted
          // the /100 divisor, producing a 100× overstated "$X win"
          // celebratory popup. Same bug class as the Positions card 100×
          // value bug. Per CLAUDE.md "Units discipline" rule: never inline
          // raw `(centi × price)` math at a render site — route through
          // lib/format.positionValueCents. Refactor 2026-05-12 lifted
          // the second line off inline BigInt math to the canonical
          // helper.
          const payoutCents = Number(pos.shares); // centi × 100¢ / 100 = centi
          const costBasis = positionValueCents(
            pos.shares,
            Number(pos.avgCostCents),
          );
          const winAmount = payoutCents - costBasis;
          if (winAmount <= 0) return;

          firedMarkets.add(event.marketSlug);
          setActiveWin({
            amountCents: winAmount,
            marketTitle: pos.marketTitle,
            marketSlug: event.marketSlug,
            outcome: pos.outcomeSortOrder === 0 ? "YES" : "NO",
            winType: classifyWinType(winAmount),
          });
        });
      } catch {
        // bootstrap failure → no auto-trigger. Manual share button
        // on DashboardTabs is unaffected.
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
      if (socket) {
        for (const slug of positionsByMarket.keys()) {
          socket.emit("unsubscribe", { slug });
        }
        socket.disconnect();
      }
    };
  }, [prefs, username]);

  if (!activeWin || !username) return null;
  return (
    <WinShareModal
      win={activeWin}
      username={username}
      onClose={() => setActiveWin(null)}
    />
  );
}
